/**
 * Creation reason types
 * Source of truth https://github.com/voiapp/repairs/blob/master/internal/domain/creation_reasons.go
 * Documentation https://voidev.atlassian.net/wiki/spaces/RND/pages/81269030949/Creation+Reasons+-+Active+List
 */

/**
 * Main task creation reason types
 */
export enum CreationReasonType {
    FM_FS_REPORT = 'FM_FS_REPORT',
    ACCIDENT_REPORT = 'ACCIDENT_REPORT',
    OPERATIONAL_CHANGE = 'OPERATIONAL_CHANGE',
    CUSTOMER_FEEDBACK = 'CUSTOMER_FEEDBACK',
    HARDWARE_SIGNAL = 'HARDWARE_SIGNAL',
    RIDER_BEHAVIOR = 'RIDER_BEHAVIOR',
    PREVENTATIVE_MAINTENANCE = 'PREVENTATIVE_MAINTENANCE',
}

/**
 * Customer feedback details
 */
export enum CustomerFeedbackDetail {
    BRAKES = 'BRAKES',
    THROTTLE = 'THROTTLE',
    WHEELS = 'WHEELS',
    FENDER = 'FENDER',
    HANDLEBARS = 'HANDLEBARS',
    STEERING = 'STEERING',
    FRAME = 'FRAME',
    BATTERY = 'BATTERY',
    MOTOR = 'MOTOR',
    LIGHTS = 'LIGHTS',
    PHONE_HOLDER = 'PHONE_HOLDER',
    KICKSTAND = 'KICKSTAND',
    SLOW = 'SLOW',
    WONT_START = 'WONT_START',
    STOPPED_DURING_RIDE = 'STOPPED_DURING_RIDE',
    DAMAGED = 'DAMAGED',
    DIRTY = 'DIRTY',
    LOOSE_PARTS = 'LOOSE_PARTS',
    NOISES = 'NOISES',
    WONT_UNLOCK = 'WONT_UNLOCK',
    NOT_STABLE = 'NOT_STABLE',
    PEDALS = 'PEDALS',
    SEAT = 'SEAT',
    WIRES = 'WIRES',
    INCONSISTENT_SPEED = 'INCONSISTENT_SPEED',
    SAFETY_CRITICAL_FEEDBACK = 'SAFETY_CRITICAL_FEEDBACK',
    ISSUE_DURING_RIDE = 'ISSUE_DURING_RIDE',
    SELF_ACCELERATION = 'SELF_ACCELERATION',
}

/**
 * Hardware signal details
 */
export enum HardwareSignalDetail {
    BRAKES = 'BRAKES',
    THROTTLE = 'THROTTLE',
    WHEELS = 'WHEELS',
    MOTOR = 'MOTOR',
    BATTERY = 'BATTERY',
    SLOW = 'SLOW',
    WONT_START = 'WONT_START',
    STOPPED_DURING_RIDE = 'STOPPED_DURING_RIDE',
    ACCELEROMETER = 'ACCELEROMETER',
}

/**
 * Rider behavior details
 */
export enum RiderBehaviorDetail {
    SHORT_RIDES = 'SHORT_RIDES',
    VEHICLE_SWITCHING = 'VEHICLE_SWITCHING',
    TOPPLED_DURING_RIDE = 'TOPPLED_DURING_RIDE',
}

/**
 * Preventative maintenance details
 */
export enum PreventativeMaintenanceDetail {
    STEERING_COLUMN = 'STEERING_COLUMN',
    FRAME_DOWNTUBE = 'FRAME_DOWNTUBE',
    FRONT_FORK = 'FRONT_FORK',
    DISTANCE_THRESHOLD = 'DISTANCE_THRESHOLD',
    TIME_THRESHOLD = 'TIME_THRESHOLD',
}

/**
 * Operational change details
 */
export enum OperationalChangeDetail {
    FLEET_SWAP = 'FLEET_SWAP',
    WINTER_STORAGE = 'WINTER_STORAGE',
    PROJECT_LEAP = 'PROJECT_LEAP',
    MARKETING = 'MARKETING',
}

/**
 * Incident report details
 */
export enum AccidentReportDetail {
    ACCIDENT = 'ACCIDENT',
    VANDALISM = 'VANDALISM',
}

/**
 * FM/FS report details
 */
export enum FMFSReportDetail {
    ERROR_CODE = 'ERROR_CODE',
    FRONT_BRAKE = 'FRONT_BRAKE',
    REAR_BRAKE = 'REAR_BRAKE',
    THROTTLE = 'THROTTLE',
    MOTOR = 'MOTOR',
    HANDLEBARS = 'HANDLEBARS',
    STEERING = 'STEERING',
    FRAME = 'FRAME',
    FENDER = 'FENDER',
    PHONE_HOLDER = 'PHONE_HOLDER',
    LIGHTS = 'LIGHTS',
    DIRTY = 'DIRTY',
    SEAT = 'SEAT',
    PEDALS = 'PEDALS',
    VANDALISED = 'VANDALISED',
    INDICATORS = 'INDICATORS',
    WHEELS = 'WHEELS',
    BATTERY = 'BATTERY',
    KICKSTAND = 'KICKSTAND',
    BASKET = 'BASKET',
    CHAIN = 'CHAIN',
    QR_CODE = 'QR_CODE',
    RUBBER_MAT = 'RUBBER_MAT',
    BATTERY_HATCH = 'BATTERY_HATCH',
    REFLECTOR = 'REFLECTOR',
    JUMPER_CABLE_ATTEMPT_FAILED = 'JUMPER_CABLE_ATTEMPT_FAILED',
}

/**
 * FM/FS report error codes (used when detail is ERROR_CODE)
 */
export enum FMFSReportErrorCode {
    Dashboard = 'DASHBOARD',
    MotorPhaseA = 'MOTOR_PHASE_A',
    MotorPhaseB = 'MOTOR_PHASE_B',
    MotorPhaseC = 'MOTOR_PHASE_C',
    AcceleratorHall = 'ACCELERATOR_HALL',
    LeftBrakeHall = 'LEFT_BRAKE_HALL',
    RightBrakeHall = 'RIGHT_BRAKE_HALL',
    MotorHall = 'MOTOR_HALL',
    BatteryVoltage = 'BATTERY_VOLTAGE',
    BmsComm = 'BMS_COMM',
    BmsPwd = 'BMS_PWD',
    BmsSerial = 'BMS_SERIAL',
    Voltage = 'VOLTAGE',
    FlashSave = 'FLASH_SAVE',
    MasterPwd = 'MASTER_PWD',
    Controller = 'CONTROLLER',
    ProgramSkip = 'PROGRAM_SKIP',
    VehicleTimeout = 'VEHICLE_TIMEOUT',
    ControllerSerial = 'CONTROLLER_SERIAL',
    Charging = 'CHARGING',
    BatteryTemp = 'BATTERY_TEMP',
    ControllerTemp = 'CONTROLLER_TEMP',
    MotorTemp = 'MOTOR_TEMP',
    BmsTempSensor = 'BMS_TEMP_SENSOR',
    ControllerBmsMismatch = 'CONTROLLER_BMS_MISMATCH',
    ControllerDashboardMismatch = 'CONTROLLER_DASHBOARD_MISMATCH',
    ControllerBatteryLockMismatch = 'CONTROLLER_BATTERY_LOCK_MISMATCH',
    ControllerBatteryLockComm = 'CONTROLLER_BATTERY_LOCK_COMM',
    BatteryCoverOpen = 'BATTERY_COVER_OPEN',
    VehicleVerification = 'VEHICLE_VERIFICATION',
    TwinRidingDetected = 'TWIN_RIDING_DETECTED',
    TurnSignal = 'TURN_SIGNAL',
    FrontLeftTurnSignal = 'FRONT_LEFT_TURN_SIGNAL',
    FrontRightTurnSignal = 'FRONT_RIGHT_TURN_SIGNAL',
    BackLeftTurnSignal = 'BACK_LEFT_TURN_SIGNAL',
    BackRightTurnSignal = 'BACK_RIGHT_TURN_SIGNAL',
    Unknown = 'UNKNOWN',
    ScooterNotConnected = 'SCOOTER_NOT_CONNECTED',
    GpsDataInvalid = 'GPS_DATA_INVALID',
    UnresponsiveVehicle = 'UNRESPONSIVE_VEHICLE',
    HubLockCommunicationAbnormal = 'HUB_LOCK_COMMUNICATION_ABNORMAL',
    BatteryCoverLockComms = 'BATTERY_COVER_LOCK_COMMS',
    BatteryCoverLockUnlock = 'BATTERY_COVER_LOCK_UNLOCK',
    BatteryTempHigh = 'BATTERY_TEMP_HIGH',
    FallingDownWhileRiding = 'FALLING_DOWN_WHILE_RIDING',
    MidRideShutdown = 'MID_RIDE_SHUTDOWN',
    TiltedWhileParking = 'TILTED_WHILE_PARKING',
    Vibration = 'VIBRATION',
}
/**
 * Union type for all detail enums
 */
export type CreationReasonDetail =
    | CustomerFeedbackDetail
    | HardwareSignalDetail
    | RiderBehaviorDetail
    | PreventativeMaintenanceDetail
    | OperationalChangeDetail
    | AccidentReportDetail
    | FMFSReportDetail
    | FMFSReportErrorCode

/**
 * Mapping of creation reason types to their available details
 */
export const CREATION_REASON_DETAILS_MAP: Record<CreationReasonType, CreationReasonDetail[]> = {
    [CreationReasonType.CUSTOMER_FEEDBACK]: Object.values(CustomerFeedbackDetail),
    [CreationReasonType.HARDWARE_SIGNAL]: Object.values(HardwareSignalDetail),
    [CreationReasonType.RIDER_BEHAVIOR]: Object.values(RiderBehaviorDetail),
    [CreationReasonType.PREVENTATIVE_MAINTENANCE]: Object.values(PreventativeMaintenanceDetail),
    [CreationReasonType.OPERATIONAL_CHANGE]: Object.values(OperationalChangeDetail),
    [CreationReasonType.ACCIDENT_REPORT]: Object.values(AccidentReportDetail),
    [CreationReasonType.FM_FS_REPORT]: Object.values(FMFSReportDetail),
}

/**
 * Helper function to get details for a creation reason type
 */
export const getCreationReasonDetails = (reasonType: CreationReasonType): CreationReasonDetail[] => {
    return CREATION_REASON_DETAILS_MAP[reasonType] || []
}

/**
 * Helper function to check if a detail is valid for a reason type
 */
export const isValidDetailForReason = (reasonType: CreationReasonType, detail: CreationReasonDetail): boolean => {
    if (reasonType === CreationReasonType.FM_FS_REPORT && isFMFSReportErrorCode(detail)) {
        return true
    }

    const validDetails = getCreationReasonDetails(reasonType)
    return validDetails.includes(detail)
}

/**
 * Type for creation reason with optional detail
 */
export interface CreationReason {
    type: CreationReasonType
    detail?: CreationReasonDetail
}

/**
 * Task types that support creation reason selection
 * TT = transport, IFQC = in_field_quality_check
 */
export type TaskTypeWithCreationReason = 'transport' | 'in_field_quality_check'

/**
 * Mapping of task types to their allowed creation reason types
 */
export const TASK_TYPE_CREATION_REASONS_MAP: Record<TaskTypeWithCreationReason, CreationReasonType[]> = {
    transport: [
        CreationReasonType.FM_FS_REPORT,
        CreationReasonType.ACCIDENT_REPORT,
        CreationReasonType.CUSTOMER_FEEDBACK,
        CreationReasonType.PREVENTATIVE_MAINTENANCE,
        CreationReasonType.OPERATIONAL_CHANGE,
    ],
    in_field_quality_check: [
        CreationReasonType.FM_FS_REPORT,
        CreationReasonType.ACCIDENT_REPORT,
        CreationReasonType.CUSTOMER_FEEDBACK,
        CreationReasonType.PREVENTATIVE_MAINTENANCE,
    ],
}

export const toLabel = (value: string): string => {
    return value
        .split('_')
        .map(word => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ')
}

export const getCreationReasonTypeLabel = (reasonType: CreationReasonType): string => {
    if (reasonType === CreationReasonType.FM_FS_REPORT) {
        return 'Fleet Specialist Report'
    }

    return toLabel(reasonType)
}

export const getCreationReasonDetailLabel = (detail: CreationReasonDetail): string => {
    if (Object.values(FMFSReportErrorCode).includes(detail as FMFSReportErrorCode)) {
        return toLabel(detail)
    }

    return toLabel(detail)
}

export const isFMFSReportErrorCode = (value: string): value is FMFSReportErrorCode => {
    return Object.values(FMFSReportErrorCode).includes(value as FMFSReportErrorCode)
}

/**
 * Helper function to get allowed creation reason types for a task type
 */
export const getAllowedCreationReasonsForTaskType = (taskType: TaskTypeWithCreationReason): CreationReasonType[] => {
    return TASK_TYPE_CREATION_REASONS_MAP[taskType] || []
}

const REPORT_ISSUE_EXCLUDED_CREATION_REASONS: CreationReasonType[] = [
    CreationReasonType.PREVENTATIVE_MAINTENANCE,
    CreationReasonType.OPERATIONAL_CHANGE,
]

export const getAllowedCreationReasonsForReportIssue = (taskType: TaskTypeWithCreationReason): CreationReasonType[] => {
    return getAllowedCreationReasonsForTaskType(taskType).filter(
        reason => !REPORT_ISSUE_EXCLUDED_CREATION_REASONS.includes(reason),
    )
}

/**
 * Helper function to check if a creation reason type is allowed for a task type
 */
export const isCreationReasonAllowedForTaskType = (
    taskType: TaskTypeWithCreationReason,
    reasonType: CreationReasonType,
): boolean => {
    const allowedReasons = getAllowedCreationReasonsForTaskType(taskType)
    return allowedReasons.includes(reasonType)
}
